import { MprcIconButton, MprcRequestErrorDialog, MprcRequestIndicator } from "components";
import { IconAudience } from "components/icons";
import { MprcRequestSnackbarErrorReport } from "components/MprcRequestErrorReport";
import { AdvCoreFilter, ManageEnrollOperation, UserInfoDto, UserInfoDtoPaginatedList } from "generated/CoreApi_v1";
import useDidUpdateEffect from "hooks/useNoInitialEffect";
import { useCoreApi } from "providers/CoreApiProvider";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { BatchEditSearchUsers } from "views/foundation/user/components/BatchEdit/BatchEditSearchUsers";
import { IAdvFilterCommand, IAdvFilterParams } from "views/general/AdvancedFilter";
import { FilterCmd } from "views/general/AdvancedFilter/FilterCmd";
import { EditEnrollmentStepProps, OPERATION_PAGE_SIZE } from "views/lms/enrollment/Home";
import { useEnrollmentManagementContext } from "views/lms/enrollment/Home/EnrollmentManagementWrapper";
import { useEnrollmentOperationContext } from "views/lms/enrollment/Home/Operation/OperationBox";

////////////////////////////////////////////////////////////////////////////
interface IViewDataFilter {
    pageNumber: number;
    filter: IAdvFilterParams<any>;
}

export const OperationSearchUsers: React.FC<EditEnrollmentStepProps> = (props) => {

    const { setLoadingSelected, setPosting, loadingUsers, setLoadingUsers, selectedUsers, setSelectedUsers } = useEnrollmentOperationContext();

    const api = useCoreApi();
    const ctx = useEnrollmentManagementContext();
    const training = ctx.training!;
    const classroom = ctx.classroom;
    const { t } = useTranslation("lms");

    const [viewError, setViewError] = useState<any>();
    const [viewData, setViewData] = useState<UserInfoDtoPaginatedList>();
    const [loading, setLoading] = useState<boolean>(false);
    const [viewFilter, setViewFilter] = useState<IViewDataFilter>({
        pageNumber: 1,
        filter: {
            searchText: "",
            advFilters: [],
            order: null
        },
    });

    const cmdRequiredAudienceToEnroll = FilterCmd.EnrollOperation.enrollOperationAudienceMandatoryToEnrollButtonOption(0);
    const cmdRequiredAudience = FilterCmd.EnrollOperation.enrollOperationAudienceMandatoryButtonOption(0);

    var advFilter = viewFilter.filter.advFilters;
    const filtered = advFilter.length > 0;

    ///
    const dropdownOptions = (): IAdvFilterCommand[] => {
        var options: IAdvFilterCommand[] = [];

        if (training.classCount > 1 && ctx.operation != ManageEnrollOperation.Enrollment) {
            options.push(FilterCmd.EnrollOperation.enrollOperationClassroom(1, ctx.training!.trainingId, false));
        }

        if (ctx.operation == ManageEnrollOperation.Cancel
            || ctx.operation == ManageEnrollOperation.AllowExtraTime
            || ctx.operation == ManageEnrollOperation.Exclude
            || ctx.operation == ManageEnrollOperation.Finalize) {
            options.push(FilterCmd.Enroll.enrolledPeriod(1));
        }

        if (ctx.operation == ManageEnrollOperation.Enrollment
            || ctx.operation == ManageEnrollOperation.ReEnrollForRecycling
            || ctx.operation == ManageEnrollOperation.AllowExtraTime) {
            options.push(FilterCmd.Enroll.conclusionPeriod(1));
        }

        if (ctx.operation == ManageEnrollOperation.Cancel || ctx.operation == ManageEnrollOperation.Finalize) {
            options.push(FilterCmd.Enroll.startedMoreThanDays(1));
        }

        options.push(FilterCmd.EnrollOperation.enrollOperationAudienceOption(2));
        options.push(FilterCmd.EnrollOperation.enrollOperationStatusEnrollment(2));
        options.push(FilterCmd.EnrollOperation.operationEnrollmentHistory(2));

        options.push(...FilterCmd.User.unitType(ctx.unitTypes, 3));

        options.push(FilterCmd.User.group(4));
        options.push(FilterCmd.User.channelMember(4));
        options.push(FilterCmd.User.topicOptional(4));
        options.push(FilterCmd.User.topicRequired(4));

        options.push(FilterCmd.User.trainingApproved(5));
        options.push(FilterCmd.User.trainingReproved(5));
        options.push(FilterCmd.User.trainingAttending(5));

        options.push(FilterCmd.User.isManager(6));
        options.push(FilterCmd.User.managerDirectSubordinates(6));
        options.push(FilterCmd.User.managerAllSubordinates(6));

        options.push(FilterCmd.User.workplace(7, ctx.userSettings));
        options.push(FilterCmd.User.tag(7));
        options.push(FilterCmd.User.interestTag(7));
        options.push(FilterCmd.User.customField(7));

        options.push(FilterCmd.User.internals(8));
        options.push(FilterCmd.User.externals(8));
        if (ctx.operation == ManageEnrollOperation.Cancel || ctx.operation == ManageEnrollOperation.Revoke || ctx.operation == ManageEnrollOperation.Exclude) {
            options.push(FilterCmd.User.blockReason(8));
        }

        if (ctx.operation == ManageEnrollOperation.Exclude) {
            options.push(FilterCmd.User.onlyInactives(8));
        }

        return options;
    };

    ///
    const addUser = (item: UserInfoDto) => {
        setPosting(true);

        api.v1.enrollmentOperation.addUserEnroll({
            trainingId: training.trainingId,
            operation: ctx.operation!,
            userIds: [item.userId]
        }).then(result => {
            if (result.data) {
                if (selectedUsers) {
                    setSelectedUsers((state) => {
                        return {
                            ...selectedUsers,
                            totalRows: (state!.totalRows ?? 0) + 1,
                            items: [{
                                userId: item.userId,
                                name: item.name,
                                thumbnailUrl: item.thumbnailUrl
                            } as UserInfoDto, ...state!.items]
                        };
                    });
                } else {
                    setLoadingSelected(true);
                }
            }
        }).catch(e => {
            MprcRequestSnackbarErrorReport(e);
        }).finally(() => setPosting(false));
    };


    const buildFilter = () => {
        return {
            trainingId: training.trainingId,
            classroomId: classroom?.classroomId ?? null,
            operation: ctx.operation!,
            useImportedUsers: ctx.options.ImportedFile,
            searchText: viewFilter.filter.searchText ?? null,
            filter: viewFilter.filter.advFilters
        };
    };

    ///
    const addAllUsers = () => {
        setPosting(true);

        api.v1.enrollmentOperation.addAllUserEnroll(buildFilter()).then(result => {
            if (result.data) setLoadingSelected(true);
        }).catch(e => {
            MprcRequestSnackbarErrorReport(e);
        }).finally(() => setPosting(false));
    };

    ///
    const clearImportedUser = () => {
        setPosting(true);

        api.v1.enrollmentOperation.clearImportedUsersEnroll({
            trainingId: training.trainingId,
            operation: ctx.operation!,
        }).then(result => {
            if (result.data) {
                ctx.setOptions((state) => { return { ...state, ImportedFile: false }; });
            }
        }).catch(e => {
            MprcRequestSnackbarErrorReport(e);
        }).finally(() => setPosting(false));
    };

    ///
    const resetFilter = () => {
        setViewFilter({
            pageNumber: 1,
            filter: {
                searchText: "",
                advFilters: [],
                order: null
            },
        });
    };

    ///
    const handleFilterButton = (cmd: IAdvFilterCommand) => {
        const isChecked = viewFilter.filter.advFilters.find(x => x.command == cmd.command && x.value == cmd.fixedValue);

        if (isChecked) {
            setViewFilter(state => ({
                ...state,
                filter: {
                    ...viewFilter.filter,
                    advFilters: viewFilter.filter.advFilters.filter(x => !(x.command == cmd.command && x.value == cmd.fixedValue))
                }
            }));
        }
        else {
            setViewFilter(state => ({
                ...state,
                filter: {
                    ...viewFilter.filter,
                    advFilters: [
                        ...viewFilter.filter.advFilters,
                        {
                            ...cmd,
                            value: cmd.fixedValue,
                            quickFilter: null,
                        }
                    ]
                }
            }));
        }
    };


    ////////////////////////////////////////////////////////////////////////////
    /// EFFECT'S
    ////////////////////////////////////////////////////////////////////////////

    /// Ao carregar os items
    useEffect(() => {
        async function getData() {
            try {
                const result = await api.v1.enrollmentOperation.getManagedEnrollmentUsers({
                    ...buildFilter(),
                    filter: advFilter as AdvCoreFilter[],
                    pageNumber: viewFilter.pageNumber,
                    pageSize: OPERATION_PAGE_SIZE,
                });
                setViewData(result.data);
            } catch (e) {
                setViewData(undefined);
                setViewError(e);
            } finally {
                setLoading(false);
                setLoadingUsers(false);
            }
        }

        if (loading) getData();
    }, [loading]);

    ///
    useEffect(() => {
        if (loadingUsers && ctx.operation != undefined) {
            resetFilter();
        }
    }, [loadingUsers]);

    /////
    useDidUpdateEffect(() => {
        if (!loading) setLoading(true);
    }, [viewFilter]);

    ////////////////////////////////////////////////////////////////////////////
    /// RENDER
    ////////////////////////////////////////////////////////////////////////////


    return (
        <>
            <MprcRequestIndicator visible={loading} type={"request"} />
            <MprcRequestErrorDialog error={viewError} onClose={() => setViewError(null)} />

            <BatchEditSearchUsers
                filterCommands={dropdownOptions()}
                importedFile={ctx.options.ImportedFile}
                additionalFilterButtons={ctx.operation == ManageEnrollOperation.Enrollment
                    ? [
                        <MprcIconButton
                            title={t("Enrollment.StepOperation.SearchUsers.ButtonRequiredAudienceToEnrollToolTip")}
                            color={viewFilter.filter.advFilters.find(x => x.command == cmdRequiredAudienceToEnroll.command && x.value == cmdRequiredAudienceToEnroll.fixedValue)
                                ? "warning"
                                : "inherit"}
                            onClick={() => handleFilterButton(cmdRequiredAudienceToEnroll)}
                        >
                            <IconAudience fixedWidth />
                        </MprcIconButton>
                    ]
                    : [
                        <MprcIconButton
                            title={t("Enrollment.StepOperation.SearchUsers.ButtonRequiredAudienceToolTip")}
                            color={viewFilter.filter.advFilters.find(x => x.command == cmdRequiredAudience.command && x.value == cmdRequiredAudience.fixedValue)
                                ? "warning"
                                : "inherit"}
                            onClick={() => handleFilterButton(cmdRequiredAudience)}
                        >
                            <IconAudience fixedWidth />
                        </MprcIconButton>
                    ]
                }
                onAddAllUsers={addAllUsers}
                onAddUser={addUser}
                onClearImportedUsers={clearImportedUser}
                onCompleteImport={success => {
                    if (success) setLoadingUsers(true);
                    ctx.setOptions((state) => { return { ...state, ImportedFile: success || state.ImportedFile }; });
                }}
                onImportFile={fileId => api.v1.enrollmentOperation.importUserFileEnroll({
                    trainingId: training.trainingId,
                    operation: ctx.operation!,
                    filtered,
                    fileId: fileId
                })}
                onImportList={users => api.v1.enrollmentOperation.importUserListEnroll({
                    trainingId: training.trainingId,
                    operation: ctx.operation!,
                    filtered,
                    userAuthFieldValues: users
                })}
                onResetFilter={resetFilter}
                viewFilter={viewFilter}
                viewData={viewData}
                onChangeFilter={filter => setViewFilter(state => ({ ...state, ...filter }))}
            />
        </>
    );
};
